import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useMerchants, useCreateMerchant, useUpdateMerchant,
  useToggleMerchantStatus, useIndustries, useBusinessCategories, useTCSStaff,
  useDeleteMerchant, useResendInviteById
} from './hooks/useMerchants';
import { ExportButton } from '../../shared/components/ExportButton';
import { useSolutions } from '../metadata/hooks/useMetadata';
import { usePermissions, Permission } from '../../shared/hooks/usePermissions';
import { useProvisionInfra } from '../storefront-onboarding/hooks/useOnboarding';
import Button from '../../shared/components/Button';
import { AddressAutocomplete } from '../../shared/components/AddressAutocomplete';
import { ColumnVisibilityDropdown, type ColumnVisibilityOption } from '../../shared/components/ColumnVisibilityDropdown';
import { useColumnVisibility } from '../../shared/hooks/useColumnVisibility';
import type { Merchant, CreateMerchantPayload, UpdateMerchantPayload, InvoicingCadence, PlatformFeeFrequency, InstallmentPlan } from './types';
import { INVOICING_CADENCE_LABELS, PLATFORM_FEE_FREQUENCY_LABELS } from './types';

// ─── Row actions dropdown ─────────────────────────────────────────────────────

interface RowActionsProps {
  merchant: Merchant;
  canEdit: boolean;
  canDeactivate: boolean;
  canDelete: boolean;
  canProvision: boolean;
  onEdit: () => void;
  onToggleStatus: () => void;
  onResendInvite: () => void;
  onDelete: () => void;
  isTogglingStatus: boolean;
  isResending: boolean;
  isDeleting: boolean;
}

function RowActions({
  merchant, canEdit, canDeactivate, canDelete, canProvision,
  onEdit, onToggleStatus, onResendInvite, onDelete,
  isTogglingStatus, isResending, isDeleting,
}: RowActionsProps) {
  const [open, setOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmProvision, setConfirmProvision] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState(false);
  // Keyed by slug; dispatches the Terraform storefront-infra workflow for this merchant.
  const provisionInfra = useProvisionInfra(merchant.slug);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setConfirmDelete(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const close = () => { setOpen(false); setConfirmDelete(false); setConfirmProvision(false); setConfirmStatus(false); };

  const handleOpen = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, left: rect.right - 192 });
    }
    setOpen((o) => !o);
    setConfirmDelete(false);
    setConfirmProvision(false);
    setConfirmStatus(false);
  };

  const itemCls = 'w-full text-left px-4 py-2 text-sm hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <button
        ref={triggerRef}
        onClick={handleOpen}
        className="p-1.5 rounded-md hover:bg-muted text-foreground transition-colors"
        aria-label="Row actions"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" />
        </svg>
      </button>

      {open && (
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 9999 }}
          className="w-48 bg-card border border-[#E5E7EB] rounded-[10px] shadow-lg overflow-hidden"
        >
          {canEdit && (
            <button className={itemCls} onClick={() => { onEdit(); close(); }}>
              Edit
            </button>
          )}
          {canDeactivate && (
            confirmStatus ? (
              <div className="px-4 py-2 space-y-1">
                <p className="text-xs text-foreground">{merchant.active ? 'Deactivate this merchant?' : 'Activate this merchant?'}</p>
                <div className="flex gap-2">
                  <button
                    disabled={isTogglingStatus}
                    onClick={() => { onToggleStatus(); close(); }}
                    className={`flex-1 py-1 text-xs font-semibold text-white rounded disabled:opacity-50 ${merchant.active ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}
                  >
                    {isTogglingStatus ? '…' : merchant.active ? 'Yes, deactivate' : 'Yes, activate'}
                  </button>
                  <button
                    onClick={() => setConfirmStatus(false)}
                    className="flex-1 py-1 text-xs border border-[#D9D9D9] rounded text-foreground hover:bg-muted"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                className={`${itemCls} ${merchant.active ? 'text-red-600' : 'text-green-700'}`}
                disabled={isTogglingStatus}
                onClick={() => setConfirmStatus(true)}
              >
                {merchant.active ? 'Deactivate' : 'Activate'}
              </button>
            )
          )}
          {canEdit && merchant.invitePending && (
            <button
              className={`${itemCls} text-amber-600`}
              disabled={isResending}
              onClick={() => { onResendInvite(); close(); }}
            >
              {isResending ? 'Sending…' : 'Resend Invite'}
            </button>
          )}
          {canProvision && (
            confirmProvision ? (
              <div className="px-4 py-2 space-y-1">
                <p className="text-xs text-foreground">Dispatch storefront infra workflow?</p>
                <div className="flex gap-2">
                  <button
                    disabled={provisionInfra.isPending}
                    onClick={() => { provisionInfra.mutate(); close(); }}
                    className="flex-1 py-1 text-xs font-semibold bg-foreground text-background rounded hover:opacity-90 disabled:opacity-50"
                  >
                    {provisionInfra.isPending ? '…' : 'Yes, provision'}
                  </button>
                  <button
                    onClick={() => setConfirmProvision(false)}
                    className="flex-1 py-1 text-xs border border-[#D9D9D9] rounded text-foreground hover:bg-muted"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                className={itemCls}
                onClick={() => setConfirmProvision(true)}
              >
                Provision infra
              </button>
            )
          )}
          {canDelete && !merchant.active && (
            <>
              <div className="border-t border-[#E5E7EB]" />
              {confirmDelete ? (
                <div className="px-4 py-2 space-y-1">
                  <p className="text-xs text-foreground">Delete permanently?</p>
                  <div className="flex gap-2">
                    <button
                      disabled={isDeleting}
                      onClick={() => { onDelete(); close(); }}
                      className="flex-1 py-1 text-xs font-semibold bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                    >
                      {isDeleting ? '…' : 'Yes, delete'}
                    </button>
                    <button
                      onClick={() => setConfirmDelete(false)}
                      className="flex-1 py-1 text-xs border border-[#D9D9D9] rounded text-foreground hover:bg-muted"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  className={`${itemCls} text-red-600`}
                  onClick={() => setConfirmDelete(true)}
                >
                  Delete permanently
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatCommission(rate: number) {
  return `${(rate * 100).toFixed(1)}%`;
}

const inputCls = 'w-full bg-background text-foreground placeholder:text-muted-foreground border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
const labelCls = 'block text-sm font-medium text-foreground mb-1';
const optionalSpan = <span className="text-muted-foreground font-normal"> (optional)</span>;

function toggleId(list: string[], id: string) {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
}

// ─── Column visibility (PE-2254) ──────────────────────────────────────────────

const MERCHANT_COLUMNS: ColumnVisibilityOption[] = [
  { id: 'name', label: 'Name', locked: true },
  { id: 'industries', label: 'Industries' },
  { id: 'commission', label: 'Commission' },
  { id: 'contractStart', label: 'Contract Start' },
  { id: 'bpmOfficer', label: 'BPM Officer' },
  { id: 'status', label: 'Status' },
  { id: 'actions', label: 'Actions', locked: true },
];

// ─── Step indicator ───────────────────────────────────────────────────────────

const CREATE_STEP_LABELS = ['Basics', 'Contract', 'Team', 'Admin'];
const EDIT_STEP_LABELS = ['Basics', 'Contract', 'Team'];

function StepIndicator({ current, labels }: { current: number; labels: string[] }) {
  return (
    <div className="flex items-center justify-between px-6 py-4 border-b bg-muted">
      {labels.map((label, i) => {
        const step = i + 1;
        const done = step < current;
        const active = step === current;
        return (
          <div key={step} className="flex items-center gap-2 flex-1">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 transition-colors ${
              done ? 'bg-black text-white' : active ? 'bg-[#28282A] text-white' : 'bg-muted text-muted-foreground'
            }`}>
              {done ? (
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : step}
            </div>
            <span className={`text-xs font-medium ${active ? 'text-foreground' : done ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
              {label}
            </span>
            {i < labels.length - 1 && (
              <div className={`flex-1 h-px mx-2 ${done ? 'bg-black' : 'bg-muted'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Create wizard ────────────────────────────────────────────────────────────

interface WizardProps { onClose: () => void }

function CreateMerchantWizard({ onClose }: WizardProps) {
  const { data: industries = [] } = useIndustries();
  const { data: categories = [] } = useBusinessCategories();
  const { data: solutions = [] } = useSolutions();
  const { data: staff = [] } = useTCSStaff();
  const createMutation = useCreateMerchant();

  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '',
    adminEmail: '',
    companyAddress: '',
    solutionId: '',
    // Sale type (PE-2252): COMMISSION keeps the existing dated-contract model (contractEnd +
    // nextReviewDate + invoicingCadence); OUTRIGHT is a one-off build price + recurring
    // platform fee, paid off via an instalment plan — sent as dedicated top-level fields
    // (customBuildPrice/platformFee/platformFeeFrequency/installmentPlan) per watchman's
    // CreateMerchantDto (PE-2252), NOT nested inside the legacy `invoicingSchedule`. The two
    // branches are mutually exclusive on the wire — the backend 400s if the wrong branch's
    // fields are present at all, so handleSubmit below must never send both.
    saleType: 'COMMISSION' as 'COMMISSION' | 'OUTRIGHT',
    commissionCharge: '',
    invoicingCadence: 'WEEKLY' as InvoicingCadence,
    contractStartDate: '',
    contractEndDate: '',
    nextContractReviewDate: '',
    partnerSuccessBlueprintUrl: '',
    // OUTRIGHT-only fields (major currency units in the UI; converted to minor units on submit).
    customBuildPrice: '',
    platformFeeAmount: '',
    platformFeeFrequency: 'MONTHLY' as PlatformFeeFrequency,
    instalmentPlanType: 'MONTHLY' as 'MONTHLY' | 'CUSTOM',
    instalmentCount: '',
    instalmentStartDate: '',
    customInstalments: [] as { dueDate: string; amount: string }[],
    bpmOfficerId: '',
    industryIds: [] as string[],
    businessCategoryIds: [] as string[],
  });

  const set = (key: keyof typeof form, val: string | string[]) =>
    setForm((f) => ({ ...f, [key]: val }));

  const validateStep = () => {
    setError('');
    if (step === 1) {
      if (!form.name.trim()) { setError('Merchant name is required'); return false; }
      if (!form.adminEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.adminEmail.trim())) {
        setError('A valid administrator email is required'); return false;
      }
      if (!form.companyAddress.trim() || form.companyAddress.trim().length < 5) { setError('Company address must be at least 5 characters'); return false; }
    }
    if (step === 2) {
      if (!form.contractStartDate) { setError('Contract start date is required'); return false; }
      if (form.saleType === 'COMMISSION') {
        const c = parseFloat(form.commissionCharge);
        if (isNaN(c) || c < 0 || c > 100) { setError('Commission must be between 0 and 100'); return false; }
        if (!form.contractEndDate) { setError('Contract end date is required'); return false; }
        if (!form.nextContractReviewDate) { setError('Next review date is required'); return false; }
      } else {
        const price = parseFloat(form.customBuildPrice);
        if (isNaN(price) || price <= 0) { setError('Custom build price is required'); return false; }
        const fee = parseFloat(form.platformFeeAmount);
        if (isNaN(fee) || fee <= 0) { setError('Platform fee is required'); return false; }
        if (form.instalmentPlanType === 'MONTHLY') {
          const count = parseInt(form.instalmentCount, 10);
          if (isNaN(count) || count < 1) { setError('Number of instalments is required'); return false; }
          if (!form.instalmentStartDate) { setError('First instalment date is required'); return false; }
        } else {
          if (form.customInstalments.length === 0) { setError('Add at least one instalment'); return false; }
          for (const inst of form.customInstalments) {
            if (!inst.dueDate || !inst.amount.trim() || isNaN(parseFloat(inst.amount))) {
              setError('Each instalment needs a due date and amount'); return false;
            }
          }
        }
      }
    }
    if (step === 3) {
      if (form.industryIds.length === 0) { setError('Select at least one industry'); return false; }
      if (form.businessCategoryIds.length === 0) { setError('Select at least one business category'); return false; }
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep((s) => s + 1); };
  const back = () => { setError(''); setStep((s) => s - 1); };

  // Money inputs are entered in major currency units (₦) and sent in minor units (kobo) —
  // same convention as the rest of the merchant contract-terms fields (see types.ts).
  const nairaToKobo = (naira: string) => Math.round(parseFloat(naira) * 100);

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (!validateStep()) return;

    const installmentPlan: InstallmentPlan | undefined = form.saleType === 'OUTRIGHT'
      ? (form.instalmentPlanType === 'MONTHLY'
        ? { type: 'MONTHLY', installmentCount: parseInt(form.instalmentCount, 10), startDate: form.instalmentStartDate }
        : { type: 'CUSTOM', installments: form.customInstalments.map((i) => ({ dueDate: i.dueDate, amount: nairaToKobo(i.amount) })) })
      : undefined;

    // saleType branches are mutually exclusive on the wire (watchman 400s if the wrong
    // branch's fields are present at all) — build each branch's fields independently rather
    // than spreading a shared object, so there's no risk of a stray field crossing over.
    const payload: CreateMerchantPayload = form.saleType === 'COMMISSION' ? {
      name: form.name.trim(),
      companyAddress: form.companyAddress.trim(),
      saleType: 'COMMISSION',
      commissionCharge: parseFloat(form.commissionCharge) / 100,
      invoicingCadence: form.invoicingCadence,
      contractEnd: form.contractEndDate,
      nextReviewDate: form.nextContractReviewDate,
      contractStartDate: form.contractStartDate,
      industryIds: form.industryIds,
      businessCategoryIds: form.businessCategoryIds,
      adminEmail: form.adminEmail.trim(),
      ...(form.partnerSuccessBlueprintUrl.trim() ? { partnerSuccessBlueprintUrl: form.partnerSuccessBlueprintUrl.trim() } : {}),
      ...(form.bpmOfficerId ? { bpmOfficerId: form.bpmOfficerId } : {}),
      ...(form.solutionId ? { solutionId: form.solutionId } : {}),
    } : {
      name: form.name.trim(),
      companyAddress: form.companyAddress.trim(),
      saleType: 'OUTRIGHT',
      customBuildPrice: nairaToKobo(form.customBuildPrice),
      platformFee: nairaToKobo(form.platformFeeAmount),
      platformFeeFrequency: form.platformFeeFrequency,
      installmentPlan: installmentPlan!,
      contractStartDate: form.contractStartDate,
      industryIds: form.industryIds,
      businessCategoryIds: form.businessCategoryIds,
      adminEmail: form.adminEmail.trim(),
      ...(form.partnerSuccessBlueprintUrl.trim() ? { partnerSuccessBlueprintUrl: form.partnerSuccessBlueprintUrl.trim() } : {}),
      ...(form.bpmOfficerId ? { bpmOfficerId: form.bpmOfficerId } : {}),
      ...(form.solutionId ? { solutionId: form.solutionId } : {}),
    };
    try {
      await createMutation.mutateAsync(payload);
      onClose();
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Something went wrong');
    }
  };

  const isLoading = createMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-5 border-b flex items-center justify-between shrink-0">
          <h2 className="text-lg font-bold text-foreground">Create Merchant</h2>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-muted-foreground">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Step indicator */}
        <StepIndicator current={step} labels={CREATE_STEP_LABELS} />

        {/* Step body */}
        {/* PE-2246 — step 1 hosts AddressAutocomplete, whose Google-rendered suggestion
            dropdown is an absolutely-positioned descendant. A scrolling ancestor here clips
            that dropdown to the visible portion of the form, with no way to scroll to the
            clipped entries (the popup itself has no independent scrollbar). Every other step
            scrolls normally — only step 1 stays overflow-visible so its dropdown can render
            past the modal bounds uncropped. */}
        <form onSubmit={handleSubmit} className={`flex-1 p-5 space-y-4 ${step === 1 ? 'overflow-visible' : 'overflow-y-auto'}`}>

          {/* ── Step 1: Basics ───────────────────────────────────── */}
          {step === 1 && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Merchant Name</label>
                  <input required className={inputCls} value={form.name} placeholder="e.g. Kente Collective"
                    onChange={(e) => set('name', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Administrator Email</label>
                  <input required type="email" placeholder="e.g. jane@merchant.com" className={inputCls}
                    value={form.adminEmail} onChange={(e) => set('adminEmail', e.target.value)} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Solution</label>
                <select className={inputCls} value={form.solutionId} onChange={(e) => set('solutionId', e.target.value)}>
                  <option value="">Select a solution…</option>
                  {solutions.filter((s) => s.isActive).map((s) => (
                    <option key={s.slug} value={s.slug}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Company Address</label>
                <AddressAutocomplete
                  value={form.companyAddress}
                  onChange={(val) => set('companyAddress', val)}
                  onPlaceSelect={(data) => set('companyAddress', data.formattedAddress)}
                  placeholder="e.g. 123 Main St, London, UK"
                  disabled={isLoading}
                />
              </div>
            </>
          )}

          {/* ── Step 2: Contract / commercial terms ──────────────── */}
          {step === 2 && (
            <>
              <div>
                <label className={labelCls}>Sale Type</label>
                <select className={inputCls} value={form.saleType}
                  onChange={(e) => set('saleType', e.target.value as 'COMMISSION' | 'OUTRIGHT')}>
                  <option value="COMMISSION">Commission</option>
                  <option value="OUTRIGHT">Outright</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Contract Start</label>
                  <input required type="date" className={inputCls} value={form.contractStartDate}
                    onChange={(e) => set('contractStartDate', e.target.value)} />
                </div>
                {form.saleType === 'COMMISSION' && (
                  <div>
                    <label className={labelCls}>Commission Rate (%)</label>
                    <input required type="number" min="0" max="100" step="0.1" placeholder="e.g. 15"
                      className={inputCls} value={form.commissionCharge}
                      onChange={(e) => set('commissionCharge', e.target.value)} />
                  </div>
                )}
              </div>

              {form.saleType === 'COMMISSION' ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Contract End</label>
                      <input required type="date" className={inputCls} value={form.contractEndDate}
                        onChange={(e) => set('contractEndDate', e.target.value)} />
                    </div>
                    <div>
                      <label className={labelCls}>Next Review Date</label>
                      <input required type="date" className={inputCls} value={form.nextContractReviewDate}
                        onChange={(e) => set('nextContractReviewDate', e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Invoicing Cadence</label>
                    <select className={inputCls} value={form.invoicingCadence} onChange={(e) => set('invoicingCadence', e.target.value)}>
                      {(Object.keys(INVOICING_CADENCE_LABELS) as InvoicingCadence[]).map((ic) => (
                        <option key={ic} value={ic}>{INVOICING_CADENCE_LABELS[ic]}</option>
                      ))}
                    </select>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Custom Build Price (₦, one-time)</label>
                      <input required type="number" min="0" step="0.01" placeholder="e.g. 500000"
                        className={inputCls} value={form.customBuildPrice}
                        onChange={(e) => set('customBuildPrice', e.target.value)} />
                    </div>
                    <div>
                      <label className={labelCls}>Platform Fee (₦, recurring)</label>
                      <input required type="number" min="0" step="0.01" placeholder="e.g. 25000"
                        className={inputCls} value={form.platformFeeAmount}
                        onChange={(e) => set('platformFeeAmount', e.target.value)} />
                    </div>
                  </div>

                  <div>
                    <label className={labelCls}>Platform Fee Frequency</label>
                    <select required className={inputCls} value={form.platformFeeFrequency}
                      onChange={(e) => set('platformFeeFrequency', e.target.value as PlatformFeeFrequency)}>
                      {(Object.keys(PLATFORM_FEE_FREQUENCY_LABELS) as PlatformFeeFrequency[]).map((f) => (
                        <option key={f} value={f}>{PLATFORM_FEE_FREQUENCY_LABELS[f]}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelCls}>Instalment Plan</label>
                    <div className="flex gap-2">
                      {(['MONTHLY', 'CUSTOM'] as const).map((plan) => (
                        <button key={plan} type="button"
                          onClick={() => set('instalmentPlanType', plan)}
                          className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                            form.instalmentPlanType === plan
                              ? 'bg-foreground text-background border-foreground'
                              : 'bg-card text-foreground border-border hover:border-foreground'
                          }`}>
                          {plan === 'MONTHLY' ? 'Monthly (system-generated)' : 'Custom'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {form.instalmentPlanType === 'MONTHLY' ? (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls}>Number of Instalments</label>
                        <input required type="number" min="1" step="1" placeholder="e.g. 6"
                          className={inputCls} value={form.instalmentCount}
                          onChange={(e) => set('instalmentCount', e.target.value)} />
                      </div>
                      <div>
                        <label className={labelCls}>First Instalment Date</label>
                        <input required type="date" className={inputCls} value={form.instalmentStartDate}
                          onChange={(e) => set('instalmentStartDate', e.target.value)} />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {form.customInstalments.map((inst, i) => (
                        <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                          <div>
                            <label className={labelCls}>Due Date</label>
                            <input required type="date" className={inputCls} value={inst.dueDate}
                              onChange={(e) => {
                                const rows = [...form.customInstalments];
                                rows[i] = { ...rows[i], dueDate: e.target.value };
                                setForm((f) => ({ ...f, customInstalments: rows }));
                              }} />
                          </div>
                          <div>
                            <label className={labelCls}>Amount (₦)</label>
                            <input required type="number" min="0" step="0.01" className={inputCls} value={inst.amount}
                              onChange={(e) => {
                                const rows = [...form.customInstalments];
                                rows[i] = { ...rows[i], amount: e.target.value };
                                setForm((f) => ({ ...f, customInstalments: rows }));
                              }} />
                          </div>
                          <button type="button"
                            onClick={() => setForm((f) => ({ ...f, customInstalments: f.customInstalments.filter((_, idx) => idx !== i) }))}
                            className="h-[38px] px-3 border border-border rounded-md text-sm text-red-600 hover:bg-red-50">
                            Remove
                          </button>
                        </div>
                      ))}
                      <button type="button"
                        onClick={() => setForm((f) => ({ ...f, customInstalments: [...f.customInstalments, { dueDate: '', amount: '' }] }))}
                        className="px-3 py-1.5 border border-border rounded-md text-sm text-foreground hover:bg-muted">
                        + Add instalment
                      </button>
                    </div>
                  )}
                </>
              )}

              <div>
                <label className={labelCls}>Partner Blueprint URL {optionalSpan}</label>
                <input type="url" placeholder="https://docs.example.com/blueprint" className={inputCls}
                  value={form.partnerSuccessBlueprintUrl}
                  onChange={(e) => set('partnerSuccessBlueprintUrl', e.target.value)} />
              </div>
            </>
          )}

          {/* ── Step 3: Team & Classification ────────────────────── */}
          {step === 3 && (
            <>
              <div>
                <label className={labelCls}>BPM Officer {optionalSpan}</label>
                <select className={inputCls} value={form.bpmOfficerId} onChange={(e) => set('bpmOfficerId', e.target.value)}>
                  <option value="">— Unassigned —</option>
                  {staff.map((u) => (
                    <option key={u.id} value={u.id}>{u.fullName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Industries</label>
                <div className="flex flex-wrap gap-2">
                  {industries.filter((i) => i.isActive).map((industry) => (
                    <button key={industry.id} type="button"
                      onClick={() => set('industryIds', toggleId(form.industryIds, industry.id))}
                      className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                        form.industryIds.includes(industry.id)
                          ? 'bg-foreground text-background border-foreground'
                          : 'bg-card text-foreground border-border hover:border-foreground'
                      }`}>
                      {industry.name}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className={labelCls}>Business Categories</label>
                <div className="flex flex-wrap gap-2">
                  {categories.filter((c) => c.isActive).map((category) => (
                    <button key={category.id} type="button"
                      onClick={() => set('businessCategoryIds', toggleId(form.businessCategoryIds, category.id))}
                      className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                        form.businessCategoryIds.includes(category.id)
                          ? 'bg-foreground text-background border-foreground'
                          : 'bg-card text-foreground border-border hover:border-foreground'
                      }`}>
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ── Step 4: Review & submit ───────────────────────────── */}
          {/* PE-2250 — the collective admin no longer collects the administrator's full name
              or phone number here: those are set by the merchant themselves, post-invite, on
              their own profile-completion screen (ResetPasswordPage's claim flow). This step
              is now just a review of what's about to be submitted, plus the invite notice. */}
          {step === 4 && (
            <>
              <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-700">
                An invite email will be sent to <strong>{form.adminEmail || '—'}</strong> once you click{' '}
                <strong>Create Merchant</strong>. The link expires in 48 hours — the administrator sets
                their password and completes their own profile (name, phone) from there.
              </div>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div>
                  <dt className="text-muted-foreground">Merchant Name</dt>
                  <dd className="font-medium text-foreground">{form.name || '—'}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Administrator Email</dt>
                  <dd className="font-medium text-foreground">{form.adminEmail || '—'}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Sale Type</dt>
                  <dd className="font-medium text-foreground">{form.saleType === 'COMMISSION' ? 'Commission' : 'Outright'}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Contract Start</dt>
                  <dd className="font-medium text-foreground">{form.contractStartDate || '—'}</dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-muted-foreground">Company Address</dt>
                  <dd className="font-medium text-foreground">{form.companyAddress || '—'}</dd>
                </div>
              </dl>
            </>
          )}

          {error && <p className="text-red-500 text-sm">{error}</p>}
        </form>

        {/* Footer */}
        <div className="p-5 border-t flex items-center justify-between shrink-0">
          <button type="button" onClick={step === 1 ? onClose : back}
            className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-md hover:bg-muted">
            {step === 1 ? 'Cancel' : '← Back'}
          </button>
          <div className="text-xs text-muted-foreground">Step {step} of {CREATE_STEP_LABELS.length}</div>
          {step < CREATE_STEP_LABELS.length ? (
            <button type="button" onClick={next}
              className="px-4 py-2 text-sm font-semibold bg-black text-white rounded-md hover:bg-gray-800">
              Next →
            </button>
          ) : (
            <Button loading={isLoading} onClick={handleSubmit}>
              Create Merchant
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Edit wizard ──────────────────────────────────────────────────────────────

interface EditFormProps { merchant: Merchant; onClose: () => void }

export function EditMerchantForm({ merchant, onClose }: EditFormProps) {
  const { data: industries = [] } = useIndustries();
  const { data: categories = [] } = useBusinessCategories();
  const { data: tcsStaff = [] } = useTCSStaff();
  const { data: solutions = [] } = useSolutions();
  const updateMutation = useUpdateMerchant();

  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: merchant.name,
    companyAddress: merchant.companyAddress,
    commissionCharge: String(Math.round(merchant.commissionCharge * 100)),
    solutionId: merchant.solutionId,
    contractStartDate: merchant.contractStartDate.slice(0, 10),
    contractEndDate: merchant.contractEndDate?.slice(0, 10) ?? '',
    nextContractReviewDate: merchant.nextContractReviewDate?.slice(0, 10) ?? '',
    partnerSuccessBlueprintUrl: merchant.partnerSuccessBlueprintUrl ?? '',
    bpmOfficerId: merchant.bpmOfficerId ?? '',
    industryIds: merchant.industries.map((i) => i.industry.id),
    businessCategoryIds: merchant.businessCategories.map((c) => c.businessCategory.id),
  });

  const set = (key: keyof typeof form, val: string | string[]) =>
    setForm((f) => ({ ...f, [key]: val }));

  const validateStep = () => {
    setError('');
    if (step === 1) {
      if (!form.name.trim()) { setError('Merchant name is required'); return false; }
      if (form.companyAddress.trim().length < 5) { setError('Company address must be at least 5 characters'); return false; }
      const c = parseFloat(form.commissionCharge);
      if (isNaN(c) || c < 0 || c > 100) { setError('Commission must be between 0 and 100'); return false; }
    }
    if (step === 2) {
      if (!form.contractStartDate) { setError('Contract start date is required'); return false; }
    }
    if (step === 3) {
      if (form.industryIds.length === 0) { setError('Select at least one industry'); return false; }
      if (form.businessCategoryIds.length === 0) { setError('Select at least one business category'); return false; }
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep((s) => s + 1); };
  const back = () => { setError(''); setStep((s) => s - 1); };

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (!validateStep()) return;
    const payload: UpdateMerchantPayload = {
      name: form.name.trim(),
      companyAddress: form.companyAddress.trim(),
      commissionCharge: parseFloat(form.commissionCharge) / 100,
      industryIds: form.industryIds,
      businessCategoryIds: form.businessCategoryIds,
      contractStartDate: form.contractStartDate,
      ...(form.contractEndDate ? { contractEndDate: form.contractEndDate } : { contractEndDate: undefined }),
      ...(form.nextContractReviewDate ? { nextContractReviewDate: form.nextContractReviewDate } : {}),
      ...(form.partnerSuccessBlueprintUrl.trim() ? { partnerSuccessBlueprintUrl: form.partnerSuccessBlueprintUrl.trim() } : {}),
      bpmOfficerId: form.bpmOfficerId || undefined,
      solutionId: form.solutionId || undefined,
    };
    try {
      await updateMutation.mutateAsync({ id: merchant.id, data: payload });
      onClose();
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Something went wrong');
    }
  };

  const isLoading = updateMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-5 border-b flex items-center justify-between shrink-0">
          <h2 className="text-lg font-bold text-foreground">Edit Merchant</h2>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-muted-foreground">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Step indicator */}
        <StepIndicator current={step} labels={EDIT_STEP_LABELS} />

        {/* Step body */}
        {/* PE-2246 — step 1 hosts AddressAutocomplete, whose Google-rendered suggestion
            dropdown is an absolutely-positioned descendant. A scrolling ancestor here clips
            that dropdown to the visible portion of the form, with no way to scroll to the
            clipped entries (the popup itself has no independent scrollbar). Every other step
            scrolls normally — only step 1 stays overflow-visible so its dropdown can render
            past the modal bounds uncropped. */}
        <form onSubmit={handleSubmit} className={`flex-1 p-5 space-y-4 ${step === 1 ? 'overflow-visible' : 'overflow-y-auto'}`}>

          {/* ── Step 1: Basics ───────────────────────────────────── */}
          {step === 1 && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Name</label>
                  <input required className={inputCls} value={form.name}
                    onChange={(e) => set('name', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Commission Rate (%)</label>
                  <input required type="number" min="0" max="100" step="0.1" placeholder="e.g. 15"
                    className={inputCls} value={form.commissionCharge}
                    onChange={(e) => set('commissionCharge', e.target.value)} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Solution</label>
                <select className={inputCls} value={form.solutionId} onChange={(e) => set('solutionId', e.target.value)}>
                  {solutions.filter((s) => s.isActive).map((s) => (
                    <option key={s.slug} value={s.slug}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Company Address</label>
                <AddressAutocomplete
                  value={form.companyAddress}
                  onChange={(val) => set('companyAddress', val)}
                  onPlaceSelect={(data) => set('companyAddress', data.formattedAddress)}
                  placeholder="e.g. 123 Main St, London, UK"
                  disabled={isLoading}
                />
              </div>
            </>
          )}

          {/* ── Step 2: Contract ─────────────────────────────────── */}
          {step === 2 && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Contract Start</label>
                  <input required type="date" className={inputCls} value={form.contractStartDate}
                    onChange={(e) => set('contractStartDate', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Contract End {optionalSpan}</label>
                  <input type="date" className={inputCls} value={form.contractEndDate}
                    onChange={(e) => set('contractEndDate', e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Next Review Date {optionalSpan}</label>
                  <input type="date" className={inputCls} value={form.nextContractReviewDate}
                    onChange={(e) => set('nextContractReviewDate', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Partner Blueprint URL {optionalSpan}</label>
                  <input type="url" placeholder="https://…" className={inputCls}
                    value={form.partnerSuccessBlueprintUrl}
                    onChange={(e) => set('partnerSuccessBlueprintUrl', e.target.value)} />
                </div>
              </div>
            </>
          )}

          {/* ── Step 3: Team & Classification ────────────────────── */}
          {step === 3 && (
            <>
              <div>
                <label className={labelCls}>BPM Officer {optionalSpan}</label>
                <select className={inputCls} value={form.bpmOfficerId}
                  onChange={(e) => set('bpmOfficerId', e.target.value)}>
                  <option value="">— Unassigned —</option>
                  {tcsStaff.map((u) => (
                    <option key={u.id} value={u.id}>{u.fullName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Industries</label>
                <div className="flex flex-wrap gap-2">
                  {industries.filter((i) => i.isActive).map((industry) => (
                    <button key={industry.id} type="button"
                      onClick={() => set('industryIds', toggleId(form.industryIds, industry.id))}
                      className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                        form.industryIds.includes(industry.id)
                          ? 'bg-foreground text-background border-foreground'
                          : 'bg-card text-foreground border-border hover:border-foreground'
                      }`}>
                      {industry.name}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className={labelCls}>Business Categories</label>
                <div className="flex flex-wrap gap-2">
                  {categories.filter((c) => c.isActive).map((category) => (
                    <button key={category.id} type="button"
                      onClick={() => set('businessCategoryIds', toggleId(form.businessCategoryIds, category.id))}
                      className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                        form.businessCategoryIds.includes(category.id)
                          ? 'bg-foreground text-background border-foreground'
                          : 'bg-card text-foreground border-border hover:border-foreground'
                      }`}>
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {error && <p className="text-red-500 text-sm">{error}</p>}
        </form>

        {/* Footer */}
        <div className="p-5 border-t flex items-center justify-between shrink-0">
          <button type="button" onClick={step === 1 ? onClose : back}
            className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-md hover:bg-muted">
            {step === 1 ? 'Cancel' : '← Back'}
          </button>
          <div className="text-xs text-muted-foreground">Step {step} of {EDIT_STEP_LABELS.length}</div>
          <div className="flex items-center gap-2">
            {step < EDIT_STEP_LABELS.length && (
              <button type="button" onClick={next}
                className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-md hover:bg-muted">
                Next →
              </button>
            )}
            <Button loading={isLoading} onClick={handleSubmit}>
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function Merchants() {
  const navigate = useNavigate();
  const { data: merchants = [], isLoading, error } = useMerchants();
  const toggleStatus = useToggleMerchantStatus();
  const { hasPermission } = usePermissions();

  const [showCreate, setShowCreate] = useState(false);
  const [editingMerchant, setEditingMerchant] = useState<Merchant | undefined>();

  const deleteMerchant = useDeleteMerchant();
  const resendInvite = useResendInviteById();

  const [displayLimit, setDisplayLimit] = useState(15);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const { isVisible: isColVisible, toggle: toggleCol, reset: resetCols } = useColumnVisibility('merchants-table', ['name', 'actions']);
  const visibleColumnCount = MERCHANT_COLUMNS.filter((c) => isColVisible(c.id)).length;

  const visibleMerchants = merchants.slice(0, displayLimit);
  const hasMore = displayLimit < merchants.length;

  useEffect(() => { setDisplayLimit(15); }, [merchants.length]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
        setIsLoadingMore(true);
        setTimeout(() => { setDisplayLimit((p) => p + 15); setIsLoadingMore(false); }, 300);
      }
    }, { threshold: 0.1 });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore]);

  const canCreate = hasPermission(Permission.MERCHANT_CREATE);
  const canEdit = hasPermission(Permission.MERCHANT_UPDATE);
  const canDeactivate = hasPermission(Permission.MERCHANT_DEACTIVATE);
  const canDelete = hasPermission(Permission.MERCHANT_DELETE);

  return (
    <div className="space-y-6 py-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Merchants</h1>
          <p className="text-sm text-foreground mt-1">{merchants.length} merchant{merchants.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* PE-2254 — client-side column show/hide, persisted per-browser. */}
          <ColumnVisibilityDropdown columns={MERCHANT_COLUMNS} isVisible={isColVisible} onToggle={toggleCol} onReset={resetCols} />
          {/* PE-2127 — platform-wide export. Not merchant-scoped, so always enabled. */}
          <ExportButton endpoint="/merchants/export" filenameBase="merchants" />
          {canCreate && (
            <Button onClick={() => setShowCreate(true)}>Add Merchant</Button>
          )}
        </div>
      </div>

      {isLoading && <div className="text-center py-12 text-foreground">Loading merchants…</div>}
      {error && <div className="text-center py-12 text-red-500">Failed to load merchants.</div>}

      {!isLoading && !error && (
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted">
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Name</th>
                  {isColVisible('industries') && <th className="text-left px-4 py-3 font-semibold text-foreground">Industries</th>}
                  {isColVisible('commission') && <th className="text-left px-4 py-3 font-semibold text-foreground">Commission</th>}
                  {isColVisible('contractStart') && <th className="text-left px-4 py-3 font-semibold text-foreground">Contract Start</th>}
                  {isColVisible('bpmOfficer') && <th className="text-left px-4 py-3 font-semibold text-foreground">BPM Officer</th>}
                  {isColVisible('status') && <th className="text-left px-4 py-3 font-semibold text-foreground">Status</th>}
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {visibleMerchants.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumnCount} className="text-center py-12 text-foreground">No merchants yet.</td>
                  </tr>
                ) : (
                  visibleMerchants.map((merchant) => (
                    <tr key={merchant.id}
                      onClick={() => navigate(`/merchants/${merchant.id}`)}
                      className="border-b border-border hover:bg-muted cursor-pointer">
                      <td className="px-4 py-3 font-medium text-foreground">{merchant.name}</td>
                      {isColVisible('industries') && (
                        <td className="px-4 py-3 text-foreground">
                          {merchant.industries.map((i) => i.industry.name).join(', ') || '—'}
                        </td>
                      )}
                      {isColVisible('commission') && <td className="px-4 py-3 text-foreground">{formatCommission(merchant.commissionCharge)}</td>}
                      {isColVisible('contractStart') && <td className="px-4 py-3 text-foreground">{formatDate(merchant.contractStartDate)}</td>}
                      {isColVisible('bpmOfficer') && <td className="px-4 py-3 text-foreground">{merchant.bpmOfficer?.fullName ?? '—'}</td>}
                      {isColVisible('status') && (
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            merchant.active ? 'bg-green-100 text-green-800' : 'bg-muted text-muted-foreground'
                          }`}>
                            {merchant.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <RowActions
                          merchant={merchant}
                          canEdit={canEdit}
                          canDeactivate={canDeactivate}
                          canDelete={canDelete}
                          canProvision={canCreate}
                          onEdit={() => setEditingMerchant(merchant)}
                          onToggleStatus={() => toggleStatus.mutate({ id: merchant.id, active: merchant.active })}
                          onResendInvite={() => resendInvite.mutate(merchant.id)}
                          onDelete={() => deleteMerchant.mutate(merchant.id)}
                          isTogglingStatus={toggleStatus.isPending}
                          isResending={resendInvite.isPending}
                          isDeleting={deleteMerchant.isPending}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div ref={sentinelRef} className="flex justify-center py-6 border-t border-border">
            {isLoadingMore && (
              <div className="w-6 h-6 border-2 border-border border-t-gray-800 rounded-full animate-spin" />
            )}
            {!hasMore && merchants.length > 0 && (
              <span className="text-xs text-muted-foreground">Showing all {merchants.length} merchants</span>
            )}
          </div>
        </div>
      )}

      {showCreate && <CreateMerchantWizard onClose={() => setShowCreate(false)} />}
      {editingMerchant && (
        <EditMerchantForm merchant={editingMerchant} onClose={() => setEditingMerchant(undefined)} />
      )}
    </div>
  );
}
